import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FiHome, 
  FiPackage, 
  FiShoppingCart, 
  FiFileText, 
  FiSettings, 
  FiBell,
  FiUser,
  FiMenu,
  FiX,
  FiLogOut,
  FiRefreshCw
} from 'react-icons/fi';
import { useWholesaleAuth } from '../../contexts/WholesaleAuthContext';
import WholesaleOverview from './WholesaleOverview';
import WholesaleProductCatalog from './WholesaleProductCatalog';
import WholesaleCartAndOrders from './WholesaleCartAndOrders';
import WholesaleInvoicesAndPayments from './WholesaleInvoicesAndPayments';
import WholesaleAccountSettings from './WholesaleAccountSettings';
import WholesaleCheckout from './WholesaleCheckout';
import styles from './Dashboard.module.css';

const WholesaleDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [customer, setCustomer] = useState(null);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentWholesaleUser, wholesaleCustomer, logout, refreshCustomer } = useWholesaleAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Set active section based on URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/catalog')) {
      setActiveSection('catalog');
    } else if (path.includes('/orders')) {
      setActiveSection('orders');
    } else if (path.includes('/invoices')) {
      setActiveSection('invoices');
    } else if (path.includes('/checkout')) {
      setActiveSection('checkout');
    } else if (path.includes('/settings')) {
      setActiveSection('settings');
    } else if (path.includes('/overview')) {
      setActiveSection('overview');
    } else if (path === '/wholesale-dashboard') {
      setActiveSection('overview');
    }
  }, [location.pathname]);

  useEffect(() => {
    // Customer data is already available from the wholesale auth context
    if (wholesaleCustomer) {
      setCustomer(wholesaleCustomer);
      setError(null);
    } else {
      setError("Wholesale customer data not available.");
    }

    // Add class to body to remove global gradient
    document.body.classList.add('wholesale-body');
    document.body.style.padding = '0';
    document.body.style.margin = '0';

    // Add viewport meta tag for mobile support
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(viewportMeta);
    } else {
      viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    }

    return () => {
      // Cleanup
      document.body.classList.remove('wholesale-body');
      document.body.style.padding = '';
      document.body.style.margin = '';
      
      // Reset viewport meta tag
      if (viewportMeta) {
        viewportMeta.content = 'width=device-width, initial-scale=1.0';
      }
    };
  }, [wholesaleCustomer]);

  const handleNavItemClick = (sectionId) => {
    const sectionUrls = {
      'overview': '/wholesale-dashboard/overview',
      'catalog': '/wholesale-dashboard/catalog',
      'orders': '/wholesale-dashboard/orders',
      'checkout': '/wholesale-dashboard/checkout',
      'invoices': '/wholesale-dashboard/invoices',
      'settings': '/wholesale-dashboard/settings'
    };
    
    navigate(sectionUrls[sectionId] || '/wholesale-dashboard/overview');
    handleMobileMenuToggle(false);
  };

  const handleMobileMenuToggle = (isOpen) => {
    setMobileMenuOpen(isOpen);
    // Prevent body scroll when menu is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect will be handled by the auth context
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleRefreshAccount = async () => {
    try {
      const refreshedCustomer = await refreshCustomer();
      if (refreshedCustomer) {
        setCustomer(refreshedCustomer);
        // Show a brief success message or visual feedback
        console.log('Account status refreshed');
      }
    } catch (error) {
      console.error('Failed to refresh account:', error);
    }
  };

  // Handle touch events for mobile menu
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (isMobileMenuOpen) {
        const menuOverlay = document.querySelector('.mobileMenuOverlay');
        if (menuOverlay && !menuOverlay.contains(e.target)) {
          handleMobileMenuToggle(false);
        }
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        handleMobileMenuToggle(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('touchstart', handleTouchStart);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  const sidebarItems = [
    { id: 'overview', icon: <FiHome />, label: 'Dashboard', notifications: 0 },
    { id: 'catalog', icon: <FiPackage />, label: 'Product Catalog', notifications: 2 },
    { id: 'orders', icon: <FiShoppingCart />, label: 'Cart & Orders', notifications: 1 },
    { id: 'invoices', icon: <FiFileText />, label: 'Invoices & Payments', notifications: 0 },
    { id: 'settings', icon: <FiSettings />, label: 'Account Settings', notifications: 0 },
  ];

  const sidebarContent = (
    <>
      <nav className={styles.nav}>
        {sidebarItems.map((item) => (
          <button
            key={item.id}
            className={`${styles.navItem} ${activeSection === item.id ? styles.active : ''}`}
            onClick={() => handleNavItemClick(item.id)}
          >
            <span className={styles.navIcon}>{item.icon}</span>
            <span className={styles.navLabel}>{item.label}</span>
            {item.notifications > 0 && (
              <span className={styles.notificationBadge}>{item.notifications}</span>
            )}
          </button>
        ))}
      </nav>
      <div className={styles.sidebarFooter}>
        <div className={styles.accountManager}>
          <div className={styles.managerAvatar}>
            <FiUser />
          </div>
          <div className={styles.managerInfo}>
            <div className={styles.managerName}>
              {wholesaleCustomer?.accountManager || wholesaleCustomer?.businessName || ''}
            </div>
            <div className={styles.managerContact}>
              {wholesaleCustomer?.accountManagerPhone || ''}
            </div>
          </div>
        </div>
        <button className={styles.logoutBtn} onClick={handleRefreshAccount}>
          <FiRefreshCw />
          <span>Refresh Account</span>
        </button>
        <button className={styles.logoutBtn} onClick={handleLogout}>
          <FiLogOut />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  // Render states
  if (loading) {
    return <div className={styles.centeredStatus}>Loading Wholesale Portal...</div>;
  }

  if (error) {
    return <div className={styles.centeredStatus}>{error}</div>;
  }

  return (
    <div className={styles.container}>
      {/* Mobile menu overlay */}
      <div className={`${styles.mobileMenuOverlay} ${isMobileMenuOpen ? styles.mobileMenuVisible : ''}`}>
        <div className={styles.mobileMenuHeader}>
          <img src="/images/logo.png" alt="Grey Official Logo" className={styles.logo} />
          <button onClick={() => handleMobileMenuToggle(false)} className={styles.mobileCloseButton}>
            <FiX />
          </button>
        </div>
        {sidebarContent}
      </div>

      {/* Desktop sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <img src="/images/logo.png" alt="Grey Official Logo" className={styles.logo} />
        </div>
        {sidebarContent}
      </div>

      <div className={styles.content}>
        {/* Mobile Header - Just hamburger menu */}
        <div className={styles.mobileHeader}>
          <button onClick={() => handleMobileMenuToggle(true)} className={styles.mobileMenuButton}>
            <FiMenu />
          </button>
        </div>

        {activeSection === 'overview' && <WholesaleOverview customer={customer} />}
        {activeSection === 'catalog' && <WholesaleProductCatalog customer={customer} />}
        {activeSection === 'orders' && <WholesaleCartAndOrders customer={customer} />}
        {activeSection === 'checkout' && <WholesaleCheckout customer={customer} />}
        {activeSection === 'invoices' && <WholesaleInvoicesAndPayments customer={customer} />}
        {activeSection === 'settings' && <WholesaleAccountSettings customer={customer} />}
        {/* Add other sections here as they are built */}
      </div>
    </div>
  );
};

export default WholesaleDashboard; 